import { useEffect, useState } from "react";
import { saveAs } from "file-saver";


let ExcelJS: any;
let html2canvas: any;
let jsPDF: any;

export const loadDependencies = async () => {
  if (!ExcelJS) ExcelJS = (await import("exceljs")).default;
  if (!html2canvas) html2canvas = (await import("html2canvas")).default;
  if (!jsPDF) {
    const jspdfModule = await import("jspdf");
    jsPDF = (jspdfModule as any).jsPDF || (jspdfModule as any).default;
  }
};

export const useLazyLoadDependencies = () => {
  useEffect(() => {
    loadDependencies();
  }, []);
};

interface DataRow {
  [key: string]: any;
}

export const useExportLogo = () => {
  const [logo, setLogo] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    fetch("/Lg.png")
      .then((res) => res.blob())
      .then((blob) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          if (isMounted) {
            setLogo((reader.result as string) || null);
          }
        };
        reader.readAsDataURL(blob);
      })
      .catch((err) => console.error("Error loading logo:", err));

    return () => {
      isMounted = false;
    };
  }, []);

  return { logo };
};

const resolveLogoBase64 = async (logo: string): Promise<string | null> => {
  if (!logo) return null;
  if (logo.startsWith("data:image/")) return logo;

  try {
    const response = await fetch(logo);
    const blob = await response.blob();
    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve((reader.result as string) || "");
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error("Error resolving logo image:", error);
    return null;
  }
};

const getImageExtension = (base64Image: string): "png" | "jpeg" => {
  return base64Image.startsWith("data:image/jpeg") ? "jpeg" : "png";
};

const normalizeExcelCellValue = (value: any) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") {
    return value.replace(/^'+/, "").trim();
  }
  return value;
};


export const exportChartTableToExcel = async (
  chartData: DataRow[],
  revenueData: DataRow[],
  fileName: string,
  sheetTitle: string,
  logo: string | null
): Promise<void> => {
  await loadDependencies();
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Data");

  
  const assetName = revenueData[0]?.ASSET_NAME || "Unknown Asset";
  const assetType = revenueData[0]?.ASSET_TYPE || "Unknown Type";

  const headers = Object.keys(chartData[0] || {});
  const totalColumns = headers.length;

  try {
    if (logo) {
      const logoBase64 = await resolveLogoBase64(logo);
      if (logoBase64) {
        const imageId = workbook.addImage({
          base64: logoBase64,
          extension: getImageExtension(logoBase64),
        });

        worksheet.addImage(imageId, {
          tl: { col: Math.floor(totalColumns / 2) - 1, row: 0 },
          ext: { width: 200, height: 60 },
        });
      }
    }
  } catch (error) {
    console.error("Error loading image:", error);
  }

  
  worksheet.mergeCells(`A3:${String.fromCharCode(65 + headers.length - 1)}3`);
  worksheet.getCell("A3").value = assetName;
  worksheet.getCell("A3").alignment = { horizontal: "left", vertical: "middle" };

  worksheet.mergeCells(`A4:${String.fromCharCode(65 + headers.length - 1)}4`);
  worksheet.getCell("A4").value = assetType;
  worksheet.getCell("A4").alignment = { horizontal: "left", vertical: "middle" };

  worksheet.addRow([]);

  
  worksheet.getCell("A1").value = sheetTitle;

  const headerRow = worksheet.addRow(headers);
  headerRow.eachCell((cell: any) => {  
    cell.font = { bold: true, color: { argb: "FFFFFF" } };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "4F81BD" } };
    cell.alignment = { horizontal: "center", vertical: "middle" };
  });

  chartData.forEach((rowData: DataRow, index: number) => {
    const row = worksheet.addRow(
      Object.values(rowData).map((value) => normalizeExcelCellValue(value))
    );
    row.eachCell((cell: any) => {  
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: index % 2 === 0 ? "D9E1F2" : "FFFFFF" },
      };
    });
  });
  
  
  worksheet.columns.forEach((col: any, i: number) => {  
    if (col) col.width = Math.min(30, headers[i]?.length + 5);
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: "application/octet-stream" });
  saveAs(blob, `${fileName}.xlsx`);
};


export const exportChartToPDF = async (
  chartId: string,
  fileName: string,
  title: string,
  revenueData: DataRow[],
  logo: string | null
): Promise<void> => {
  await loadDependencies();
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });

  try {
    if (logo) {
      const logoBase64 = await resolveLogoBase64(logo);
      if (logoBase64) {
        doc.addImage(
          logoBase64,
          getImageExtension(logoBase64).toUpperCase(),
          120,
          10,
          60,
          20
        );
      }
    }
  } catch (error) {
    console.error("Error loading logo:", error);
  }

  const assetName = revenueData[0]?.ASSET_NAME || "Unknown Asset";
  const assetType = revenueData[0]?.ASSET_TYPE || "Unknown Type";

  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.text(title, 12, 25);

  doc.setFontSize(14);
  doc.text(`Asset Name: ${assetName}`, 14, 40);
  doc.text(`Asset Type: ${assetType}`, 14, 48);

  const chartElement = document.getElementById(chartId);
  if (!chartElement) {
    console.error(`Element with ID ${chartId} not found.`);
    return;
  }

  const buttons = chartElement.querySelectorAll("button");
  buttons.forEach((btn) => (btn.style.display = "none"));

  try {
    const canvas = await html2canvas(chartElement as HTMLElement, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#fff",
    } as any);

    const imgData = canvas.toDataURL("image/png");
    doc.addImage(imgData, "PNG", 10, 70, 270, 130);

    buttons.forEach((btn) => (btn.style.display = "block"));
    doc.save(`${fileName}.pdf`);
  } catch (error) {
    console.error("Error capturing chart:", error);
    buttons.forEach((btn) => (btn.style.display = "block"));
  }
};

export const copyChartToClipboard = async (
  chartId: string,
  chartData: DataRow[],
  revenueData: DataRow[]
): Promise<void> => {
  await loadDependencies();
  
  const chartElement = document.getElementById(chartId);
  if (!chartElement) {
    console.error(`Chart with ID ${chartId} not found.`);
    return;
  }

  const buttons = chartElement.querySelectorAll("button");
  buttons.forEach((btn: HTMLButtonElement) => (btn.style.display = "none"));

  try {
    
    const canvas = await (html2canvas as any)(chartElement, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#fff",
    });

    const imageBase64 = canvas.toDataURL("image/png"); 

    
    let textData = `<p><b>Asset:</b> ${revenueData[0]?.ASSET_NAME || "Unknown"}</p>`;
    textData += `<p><b>Type:</b> ${revenueData[0]?.ASSET_TYPE || "Unknown"}</p>`;
    textData += `<table border="1"><tr>${Object.keys(chartData[0] || {})
      .map((key) => `<th>${key}</th>`)
      .join("")}</tr>`; 
    
    chartData.forEach((row) => {
      textData += `<tr>${Object.values(row)
        .map((val) => `<td>${val}</td>`)
        .join("")}</tr>`; 
    });

    textData += `</table>`;

    
    const htmlContent = `<html><body>
      <img src="${imageBase64}" width="600"/><br/>${textData}
    </body></html>`;

    
    await navigator.clipboard.write([
      new ClipboardItem({
        "text/html": new Blob([htmlContent], { type: "text/html" }),
      }),
    ]);

    // alert("Chart image and data copied! You can now paste them.");
  } catch (error) {
    console.error("Error copying chart:", error);
  } finally {
    buttons.forEach((btn: HTMLButtonElement) => (btn.style.display = "block"));
  }
};


export const exportTableOldToCSV = (chartData: DataRow[], fileName: string): void => {
  if (!chartData.length) {
    console.error("No data available to export.");
    return;
  }

  // const headers = Object.keys(chartData[0]);
    const headers = Object.keys(chartData[0] || {});
  const csvRows: string[] = [];

  csvRows.push(headers.join(","));

  chartData.forEach((row: DataRow) => {
    const values = headers.map((header: string) => `"${row[header as keyof DataRow] || ""}"`);
    csvRows.push(values.join(","));
  });

  const csvContent = csvRows.join("\n");
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });

  saveAs(blob, `${fileName}.csv`);
};

export const exportTableToCSV = (chartData: DataRow[], fileName: string): void => {
  if (!chartData.length) {
    console.error("No data available to export.");
    return;
  }

  // const headers = Object.keys(chartData[0]);
  const headers = Object.keys(chartData[0] || {});
  const csvRows: string[] = [];

  const toCsvCell = (value: any) => {
    if (value === null || value === undefined) return "\"\"";
    const normalized =
      typeof value === "string"
        ? value.replace(/^'+/, "").trim()
        : String(value);
    return `"${normalized.replace(/"/g, "\"\"")}"`;
  };

  csvRows.push(headers.join(","));

  chartData.forEach((row: DataRow) => {
    const values = headers.map((header: string) => {
      let value = row[header];

    
      if (
        header.toLowerCase().includes("time") ||
        header.toLowerCase().includes("date")
      ) {
        if (value) {
          const d = new Date(value);
          if (!isNaN(d.getTime())) {
            value = d.toLocaleString();
          }
        }
      }

      return toCsvCell(value);
    });

    csvRows.push(values.join(","));
  });

  const csvContent = csvRows.join("\n");
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });

  saveAs(blob, `${fileName}.csv`);
};

export const exportTableToPDF = async (
  tableId: string,
  fileName: string,
  title: string,
  revenueData: DataRow[], 
  logo: string | null
): Promise<void> => {
  await loadDependencies();
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });

  try {
    if (logo) {
      const logoBase64 = await resolveLogoBase64(logo);
      if (logoBase64) {
        doc.addImage(
          logoBase64,
          getImageExtension(logoBase64).toUpperCase(),
          120,
          10,
          60,
          20
        );
      }
    }
  } catch (error) {
    console.error("Error loading logo:", error);
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.text(title, 12, 25);

  const assetName = revenueData.length > 0 ? revenueData[0].ASSET_NAME || null : null;
  const assetType = revenueData.length > 0 ? revenueData[0].ASSET_TYPE || null : null;
  
  doc.setFontSize(12);
  
  if (assetName) {
    doc.text(`Asset Name: ${assetName}`, 12, 35);
  }
  
  if (assetType) {
    doc.text(`Asset Type: ${assetType}`, 12, assetName ? 42 : 35); 
  }

  const tableElement = document.getElementById(tableId);
  if (!tableElement) {
    console.error(`Element with ID ${tableId} not found.`);
    return;
  }

  const originalWidth = tableElement.style.width;

  const buttons = tableElement.querySelectorAll("button, .export-icons");
  buttons.forEach((btn) => {
    (btn as HTMLElement).style.display = "none";
  });

  try {
    tableElement.style.width = `${tableElement.scrollWidth}px`;

    const canvas = await html2canvas(tableElement as HTMLElement, {
      scale: 2,
      scrollX: 0,
      scrollY: 0,
      useCORS: true,
      backgroundColor: "#fff",
      windowWidth: tableElement.scrollWidth,
    });

    const imgData = canvas.toDataURL("image/png");
    doc.addImage(imgData, "PNG", 10, 50, 270, 140);

    doc.save(`${fileName}.pdf`);
  } catch (error) {
    console.error("Error capturing table:", error);
  } finally {
    
    tableElement.style.width = originalWidth;

    
    buttons.forEach((btn) => {
      (btn as HTMLElement).style.display = "block";
    });
  }
};

function parseTableToMatrix(table: HTMLTableElement): string[][] {
  const matrix: string[][] = [];
  const spanMap: { [key: string]: { value: string; rowspan: number } } = {};

  const rows = table.querySelectorAll("tr");
  rows.forEach((row, rowIndex) => {
    const cells = row.children;
    matrix[rowIndex] = [];

    let colIndex = 0;
    for (let cellIndex = 0; cellIndex < cells.length; cellIndex++) {
      const cell = cells[cellIndex] as HTMLTableCellElement;

      while (matrix[rowIndex][colIndex] !== undefined) {
        colIndex++;
      }

      const cellValue = cell.textContent?.trim() || "";
      const colspan = parseInt(cell.getAttribute("colspan") || "1", 10);
      const rowspan = parseInt(cell.getAttribute("rowspan") || "1", 10);

      for (let i = 0; i < colspan; i++) {
        matrix[rowIndex][colIndex + i] = cellValue;
       
        if (rowspan > 1) {
  for (let j = 1; j < rowspan; j++) {
    const key = `${rowIndex + j}_${colIndex + i}`;

    spanMap[key] = {
      value: rowIndex === 0 ? "" : cellValue,
      rowspan: rowspan - j,
    };
  }
}
      }

      colIndex += colspan;
    }

    for (let i = 0; i < matrix[rowIndex].length + 10; i++) {
      const key = `${rowIndex}_${i}`;
      if (matrix[rowIndex][i] === undefined && spanMap[key]) {
        matrix[rowIndex][i] = spanMap[key].value;
      }
    }
  });

  return matrix;
}

export const exportTableToExcel = async (
  fileName: string,
  title: string,
  revenueData: DataRow[],
  logo: string | null,
  groupedHeaders: string[],
  metricHeaders: string[],
  rows: DataRow[]
): Promise<void> => {
  await loadDependencies();

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Report");

  if (logo) {
    try {
      const logoBase64 = await resolveLogoBase64(logo);
      if (logoBase64) {
        const imageId = workbook.addImage({
          base64: logoBase64,
          extension: getImageExtension(logoBase64),
        });

        worksheet.addImage(imageId, {
          tl: { col: 0, row: 0 },
          ext: { width: 220, height: 50 },
        });
      }
    } catch (e) {
      console.error("Logo error:", e);
    }
  }

  worksheet.addRow([]);
  worksheet.addRow([]);

  if (revenueData.length > 0) {
    const { ASSET_NAME, ASSET_TYPE } = revenueData[0];

    if (ASSET_NAME) worksheet.addRow(["Asset Name:", ASSET_NAME]);
    if (ASSET_TYPE) worksheet.addRow(["Asset Type:", ASSET_TYPE]);

    worksheet.addRow([]);
  }

  const topHeader = ["Time", ...groupedHeaders];
  const topRow = worksheet.addRow(topHeader);

  const secondHeader = ["Time", ...metricHeaders];
  const secondRow = worksheet.addRow(secondHeader);

  [topRow, secondRow].forEach((row) => {
    row.eachCell((cell: any) => {
      cell.font = { bold: true };
      cell.alignment = { horizontal: "center", vertical: "middle" };
    });
  });

  let startCol = 2;
  let current = groupedHeaders[0];
  let spanStart = startCol;

  for (let i = 1; i <= groupedHeaders.length; i++) {
    if (groupedHeaders[i] !== current) {
      if (i + 1 - spanStart > 1) {
        worksheet.mergeCells(topRow.number, spanStart, topRow.number, i + 1);
      }
      current = groupedHeaders[i];
      spanStart = i + 2;
    }
  }

  // Data rows
  rows.forEach((row) => {
    const firstColumnKey = Object.keys(row)[0];
    const rowData = [
      normalizeExcelCellValue(row[firstColumnKey]),
      ...metricHeaders.map((metric) => normalizeExcelCellValue(row[metric] ?? "-")),
    ];
    worksheet.addRow(rowData);
  });

  // Widths
  const totalCols = ["Time", ...metricHeaders].length;

for (let i = 1; i <= totalCols; i++) {
  const column = worksheet.getColumn(i);

  let maxLength = 10;

  column.eachCell?.({ includeEmpty: true }, (cell: any) => {
    const val = cell.value ? cell.value.toString() : "";
    maxLength = Math.max(maxLength, val.length);
  });

  column.width = maxLength + 2;
}
  // worksheet.columns.forEach((col: any, i: number) => {
  //   col.width = i === 0 ? 24 : 16;
  // });

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  saveAs(blob, `${fileName}.xlsx`);
};
export const exportTableOldToExcel = async (
  tableId: string,
  fileName: string,
  title: string,
  revenueData: DataRow[],
  logo: string | null,
  excludedColumns: string[]
): Promise<void> => {
  await loadDependencies();
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Report");

  if (logo) {
    try {
      const logoBase64 = await resolveLogoBase64(logo);
      if (logoBase64) {
        const imageId = workbook.addImage({
          base64: logoBase64,
          extension: getImageExtension(logoBase64),
        });
        worksheet.addImage(imageId, { tl: { col: 0, row: 0 }, br: { col: 2, row: 2 } });
        worksheet.addRow([]);
      }
    } catch (e) {
      console.error("Logo error:", e);
    }
  }

  if (revenueData.length > 0) {
    const { ASSET_NAME, ASSET_TYPE } = revenueData[0];
  
    if (ASSET_NAME) {
      worksheet.addRow(["Asset Name:", ASSET_NAME]);
    }
  
    if (ASSET_TYPE) {
      worksheet.addRow(["Asset Type:", ASSET_TYPE]);
    }

    if (ASSET_NAME || ASSET_TYPE) {
      worksheet.addRow([]);
    }
  }
  

  const table = document.getElementById(tableId) as HTMLTableElement;
  if (!table) return console.error("Table not found.");

  const matrix = parseTableToMatrix(table);

  const lastHeaderRow = matrix.find(row => row.length && row.some(cell => cell !== ""));
  const excludedIndexes = new Set<number>();
  if (lastHeaderRow) {
    lastHeaderRow.forEach((val, idx) => {
      if (excludedColumns.includes(val)) {
        excludedIndexes.add(idx);
      }
    });
  }

  matrix.forEach((row, i) => {
    const filtered = row.filter((_, colIndex) => !excludedIndexes.has(colIndex));
    const excelRow = worksheet.addRow(filtered);
    if (i < 2) excelRow.font = { bold: true };
  });

 
  const columnCount = worksheet.getRow(worksheet.lastRow?.number || 1).cellCount;
  worksheet.columns = Array(columnCount).fill({ width: 20 });

  
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  saveAs(blob, `${fileName}.xlsx`);
};
